"use client";

import Link from "next/link";
import { useCallback, useEffect, useId, useRef, useState } from "react";

export type TocStickyItem = { id: string; title: string; readMinutes: number };

type Props = {
  items: TocStickyItem[];
  /** Fragment id for first TL;DR block (e.g. article-answer-first) */
  answerAnchor: string;
  showJumpAnswer?: boolean;
};

/**
 * Sticky TOC with active section (IntersectionObserver). Mobile: floating FAB toggles panel.
 */
export function BlogArticleTocSticky({ items, answerAnchor, showJumpAnswer = true }: Props) {
  const panelId = useId();
  const [active, setActive] = useState<string | null>(items[0]?.id ?? null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const obsRef = useRef<IntersectionObserver | null>(null);

  const onIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
    if (visible[0]?.target?.id) {
      setActive(visible[0].target.id);
    }
  }, []);

  useEffect(() => {
    const opts: IntersectionObserverInit = { root: null, rootMargin: "-45% 0px -45% 0px", threshold: [0, 0.1, 0.25] };
    obsRef.current = new IntersectionObserver(onIntersect, opts);
    const obs = obsRef.current;
    for (const it of items) {
      const el = document.getElementById(it.id);
      if (el) obs.observe(el);
    }
    return () => obs.disconnect();
  }, [items, onIntersect]);

  if (items.length < 2) return null;

  const jumpAnswer = () => {
    const el = document.getElementById(answerAnchor);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileOpen(false);
  };

  const list = (
    <>
      <p className="ed-blog-toc-sticky__label">In this guide</p>
      <ol className="ed-blog-toc-sticky__list">
        {items.map((it) => (
          <li key={it.id}>
            <a
              href={`#${it.id}`}
              className={active === it.id ? "ed-blog-toc-sticky__a is-active" : "ed-blog-toc-sticky__a"}
              onClick={() => setMobileOpen(false)}
            >
              <span className="ed-blog-toc-sticky__title">{it.title}</span>
              <span className="ed-blog-toc-sticky__time">{it.readMinutes} min</span>
            </a>
          </li>
        ))}
      </ol>
      {showJumpAnswer ? (
        <button type="button" className="ed-blog-toc-sticky__jump" onClick={jumpAnswer}>
          Jump to answer
        </button>
      ) : null}
    </>
  );

  return (
    <>
      <aside className="ed-blog-toc-sticky ed-blog-toc-sticky--desktop" aria-label="On this page">
        {list}
      </aside>

      <div className="ed-blog-toc-sticky__mobile">
        <button
          type="button"
          className="ed-blog-toc-sticky__fab"
          aria-expanded={mobileOpen}
          aria-controls={panelId}
          onClick={() => setMobileOpen((o) => !o)}
        >
          Contents
        </button>
        {mobileOpen ? (
          <div id={panelId} className="ed-blog-toc-sticky__sheet" role="dialog" aria-label="Table of contents">
            {list}
            <Link href="/blog" className="ed-blog-toc-sticky__close-sheet" onClick={() => setMobileOpen(false)}>
              Close
            </Link>
          </div>
        ) : null}
      </div>
    </>
  );
}
